import * as THREE from 'three';
import {
  BloomEffect,
  Effect,
  EffectComposer,
  EffectPass,
  RenderPass,
  SMAAEffect,
  SMAAPreset,
  ToneMappingEffect,
  ToneMappingMode,
  VignetteEffect,
} from 'postprocessing';
import type { BiomeLook } from './Lighting';
import type { ResolutionState } from './autoQuality';
import { QUALITY, type Quality, renderCtx, setRenderQuality } from './RenderContext';

export type { Quality } from './RenderContext';

export interface GradeSettings {
  saturation: number;
  contrast: number;
  exposure: number;
  shadowTint: THREE.Color;
  highlightTint: THREE.Color;
}

const gradeFrag = /* glsl */ `
uniform float uSat;
uniform float uContrast;
uniform float uExposure;
uniform vec3 uShadowTint;
uniform vec3 uHighlightTint;
uniform float uFlash;
uniform vec3 uFlashColor;
uniform float uDesat;

void mainImage(const in vec4 inputColor, const in vec2 uv, out vec4 outputColor) {
  vec3 c = inputColor.rgb * uExposure;
  float l = dot(c, vec3(0.2126, 0.7152, 0.0722));
  c = mix(vec3(l), c, uSat * (1.0 - uDesat));
  c = (c - 0.5) * uContrast + 0.5;
  // Split toning: tinted blacks, warm or icy highlights.
  c += uShadowTint * (1.0 - smoothstep(0.0, 0.45, l));
  c += uHighlightTint * smoothstep(0.55, 1.0, l);
  c = mix(c, uFlashColor, uFlash);
  outputColor = vec4(clamp(c, 0.0, 1.0), inputColor.a);
}
`;

/** Per-biome colour grading applied after tone mapping. */
export class GradeEffect extends Effect {
  constructor() {
    super('GradeEffect', gradeFrag, {
      uniforms: new Map<string, THREE.Uniform>([
        ['uSat', new THREE.Uniform(0.92)],
        ['uContrast', new THREE.Uniform(1.06)],
        ['uExposure', new THREE.Uniform(1.08)],
        ['uShadowTint', new THREE.Uniform(new THREE.Color(0.0, 0.012, 0.02))],
        ['uHighlightTint', new THREE.Uniform(new THREE.Color(0.02, 0.012, 0.0))],
        ['uFlash', new THREE.Uniform(0)],
        ['uFlashColor', new THREE.Uniform(new THREE.Color(1, 1, 1))],
        ['uDesat', new THREE.Uniform(0)],
      ]),
    });
  }

  private u<T>(name: string): THREE.Uniform<T> {
    return this.uniforms.get(name) as THREE.Uniform<T>;
  }

  apply(g: Partial<GradeSettings>): void {
    if (g.saturation !== undefined) this.u<number>('uSat').value = g.saturation;
    if (g.contrast !== undefined) this.u<number>('uContrast').value = g.contrast;
    if (g.exposure !== undefined) this.u<number>('uExposure').value = g.exposure;
    if (g.shadowTint) this.u<THREE.Color>('uShadowTint').value.copy(g.shadowTint);
    if (g.highlightTint) this.u<THREE.Color>('uHighlightTint').value.copy(g.highlightTint);
  }

  set flash(v: number) {
    this.u<number>('uFlash').value = v;
  }

  set flashColor(c: THREE.Color) {
    this.u<THREE.Color>('uFlashColor').value.copy(c);
  }

  /** 0..1 greyscale (death screen). */
  set desaturate(v: number) {
    this.u<number>('uDesat').value = v;
  }
}

/** Frame statistics for the performance probe (`window.__AF__.renderer.stats`). */
export interface RenderStats {
  /** Draw calls of the last frame, all passes (shadow maps, scene, post). */
  calls: number;
  triangles: number;
  /** Smoothed frame interval (ms) and the current canvas pixel ratio. */
  frameMs: number;
  pixelRatio: number;
  programs: number;
}

/**
 * Owns the WebGL renderer and the post-processing chain. Everything is
 * rendered in half-float HDR so emissive VFX (> 1.0) bloom while the world
 * stays neutral, matching the "neutral world, saturated light" look.
 *
 * Quality presets (`RenderContext.QUALITY`) change resolution, shadows, bloom
 * depth, anti-aliasing and image-based light here; zones read the same
 * profile for point lights, particles and creature shadows. On top of the
 * preset cap, a dynamic-resolution governor lowers the pixel ratio when frames
 * run long and restores it when they recover.
 */
export class GameRenderer {
  readonly renderer: THREE.WebGLRenderer;
  readonly composer: EffectComposer;
  readonly bloom: BloomEffect;
  readonly grade: GradeEffect;
  readonly vignette: VignetteEffect;
  readonly stats: RenderStats = { calls: 0, triangles: 0, frameMs: 16.7, pixelRatio: 1, programs: 0 };
  /** Photosensitivity option: scales full-screen flashes down. */
  flashReduction = false;
  private renderPass: RenderPass;
  private mainPass: EffectPass;
  private smaaPass: EffectPass;
  private flashT = 0;
  private flashDur = 0;
  private flashPeak = 0;
  quality: Quality = 'high';
  private pmrem: THREE.PMREMGenerator;
  private envCache = new Map<string, THREE.WebGLRenderTarget>();
  private look: BiomeLook | null = null;
  /** Dynamic resolution: current pixel ratio, and slow/fast frame streaks. */
  private dpr = 1;
  private slowT = 0;
  private fastT = 0;
  private lastFrame = 0;
  /** Consecutive very long frames (a run of them is a slow device, not a hitch). */
  private longFrames = 0;

  constructor(
    readonly canvasParent: HTMLElement,
    public scene: THREE.Scene,
    public camera: THREE.PerspectiveCamera,
  ) {
    this.renderer = new THREE.WebGLRenderer({
      antialias: false,
      stencil: false,
      depth: false,
      powerPreference: 'high-performance',
      preserveDrawingBuffer: false,
    });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.NoToneMapping;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    this.renderer.info.autoReset = false;
    this.renderer.domElement.id = 'game-canvas';
    canvasParent.appendChild(this.renderer.domElement);
    this.pmrem = new THREE.PMREMGenerator(this.renderer);
    renderCtx.camera = camera;

    this.composer = new EffectComposer(this.renderer, { frameBufferType: THREE.HalfFloatType });
    this.renderPass = new RenderPass(scene, camera);
    this.composer.addPass(this.renderPass);
    this.bloom = new BloomEffect({
      mipmapBlur: true,
      luminanceThreshold: 0.82,
      luminanceSmoothing: 0.22,
      intensity: 1.15,
      radius: 0.72,
    });
    this.grade = new GradeEffect();
    this.vignette = new VignetteEffect({ offset: 0.3, darkness: 0.5 });
    const toneMapping = new ToneMappingEffect({ mode: ToneMappingMode.ACES_FILMIC });
    this.mainPass = new EffectPass(camera, this.bloom, toneMapping, this.grade, this.vignette);
    this.composer.addPass(this.mainPass);
    this.smaaPass = new EffectPass(camera, new SMAAEffect({ preset: SMAAPreset.HIGH }));
    this.composer.addPass(this.smaaPass);

    this.setQuality('high');
    this.resize();
    window.addEventListener('resize', () => this.resize());
  }

  setScene(scene: THREE.Scene): void {
    this.scene = scene;
    this.renderPass.mainScene = scene;
    this.look = (scene.userData.look as BiomeLook | undefined) ?? null;
    this.applyLook();
  }

  /** Grade, bloom threshold and image-based light for the current zone look. */
  private applyLook(): void {
    const look = this.look;
    const q = QUALITY[this.quality];
    if (look) {
      const g = look.grade;
      this.grade.apply({
        saturation: g.saturation,
        contrast: g.contrast,
        exposure: g.exposure,
        shadowTint: new THREE.Color(...g.shadowTint),
        highlightTint: new THREE.Color(...g.highlightTint),
      });
      this.bloom.luminanceMaterial.threshold = look.bloomThreshold ?? 0.82;
    }
    if (look && q.envMap) {
      this.scene.environment = this.envFor(look);
      this.scene.environmentIntensity = look.envIntensity;
    } else {
      this.scene.environment = null;
    }
  }

  /** Prefiltered sky-gradient environment (sky above, fog at the horizon, ground bounce below, a sun spot). */
  private envFor(look: BiomeLook): THREE.Texture {
    const key = `${look.sky}-${look.fogColor}-${look.groundBounce}-${look.sunColor}-${look.sunDir.join(',')}`;
    let rt = this.envCache.get(key);
    if (!rt) {
      const env = new THREE.Scene();
      const geo = new THREE.SphereGeometry(10, 32, 16);
      const pos = geo.getAttribute('position') as THREE.BufferAttribute;
      const cols = new Float32Array(pos.count * 3);
      const sky = new THREE.Color(look.sky);
      const hor = new THREE.Color(look.fogColor).lerp(sky, 0.35);
      const gnd = new THREE.Color(look.groundBounce);
      const c = new THREE.Color();
      for (let i = 0; i < pos.count; i++) {
        const y = pos.getY(i) / 10;
        if (y >= 0) c.copy(hor).lerp(sky, Math.min(1, y * 1.6));
        else c.copy(hor).lerp(gnd, Math.min(1, -y * 3));
        cols[i * 3] = c.r;
        cols[i * 3 + 1] = c.g;
        cols[i * 3 + 2] = c.b;
      }
      geo.setAttribute('color', new THREE.BufferAttribute(cols, 3));
      const dome = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.BackSide }));
      const sun = new THREE.Mesh(new THREE.SphereGeometry(1.2, 12, 8), new THREE.MeshBasicMaterial({ color: new THREE.Color(look.sunColor).multiplyScalar(6) }));
      sun.position.set(...look.sunDir).normalize().multiplyScalar(8);
      env.add(dome, sun);
      rt = this.pmrem.fromScene(env, 0.02);
      geo.dispose();
      (dome.material as THREE.Material).dispose();
      sun.geometry.dispose();
      (sun.material as THREE.Material).dispose();
      this.envCache.set(key, rt);
    }
    return rt.texture;
  }

  setQuality(q: Quality): void {
    this.quality = q;
    setRenderQuality(q);
    const p = QUALITY[q];
    const shadowsChanged = this.renderer.shadowMap.enabled !== p.shadows;
    this.renderer.shadowMap.enabled = p.shadows;
    if (shadowsChanged) this.renderer.shadowMap.needsUpdate = true;
    this.bloom.intensity = q === 'low' ? 0.9 : 1.15;
    this.bloom.mipmapBlurPass.levels = q === 'low' ? 4 : q === 'medium' ? 6 : 8;
    // The composer only flags its last pass for the screen: move that flag when SMAA is off.
    this.smaaPass.enabled = p.smaa;
    this.smaaPass.renderToScreen = p.smaa;
    this.mainPass.renderToScreen = !p.smaa;
    this.vignette.darkness = q === 'low' ? 0.45 : 0.5;
    this.dpr = this.maxPixelRatio();
    this.slowT = 0;
    this.fastT = 0;
    this.applyLook();
    this.resize();
  }

  private maxPixelRatio(): number {
    return Math.min(window.devicePixelRatio || 1, QUALITY[this.quality].pixelRatioCap);
  }

  resize(): void {
    const w = this.canvasParent.clientWidth || window.innerWidth;
    const h = this.canvasParent.clientHeight || window.innerHeight;
    this.camera.aspect = w / Math.max(1, h);
    this.camera.updateProjectionMatrix();
    this.renderer.setPixelRatio(this.dpr);
    this.renderer.setSize(w, h, false);
    this.composer.setSize(w, h);
    this.stats.pixelRatio = this.dpr;
  }

  /** Whether dynamic resolution can still lower or raise the pixel ratio at this quality. */
  resolutionState(): ResolutionState {
    const cap = this.maxPixelRatio();
    const min = Math.min(cap, QUALITY[this.quality].pixelRatioMin);
    return { atFloor: this.dpr <= min + 1e-3, atCap: this.dpr >= cap - 1e-3 };
  }

  /**
   * Dynamic resolution: after 2 s of long frames (> 20 ms) drop the pixel
   * ratio one step; after 6 s of fast ones (< 15 ms) raise it again, never
   * leaving the preset's [min, cap] range.
   */
  private govern(now: number): void {
    let ms = this.lastFrame > 0 ? now - this.lastFrame : 16.7;
    this.lastFrame = now;
    // Ignore hitches (tab switches, loading), but not a device that is this slow on every frame.
    if (ms > 250) {
      this.longFrames++;
      if (this.longFrames < 3) return;
      ms = 250;
    } else {
      this.longFrames = 0;
    }
    this.stats.frameMs += (ms - this.stats.frameMs) * 0.05;
    const dt = ms / 1000;
    const p = QUALITY[this.quality];
    const cap = this.maxPixelRatio();
    const min = Math.min(cap, p.pixelRatioMin);
    if (this.stats.frameMs > 20) {
      this.slowT += dt;
      this.fastT = 0;
    } else if (this.stats.frameMs < 15) {
      this.fastT += dt;
      this.slowT = 0;
    } else {
      this.slowT = Math.max(0, this.slowT - dt);
      this.fastT = Math.max(0, this.fastT - dt);
    }
    if (this.slowT > 2 && this.dpr > min + 1e-3) {
      this.dpr = Math.max(min, this.dpr - 0.25);
      this.slowT = 0;
      this.resize();
    } else if (this.fastT > 6 && this.dpr < cap - 1e-3) {
      this.dpr = Math.min(cap, this.dpr + 0.25);
      this.fastT = 0;
      this.resize();
    }
  }

  /** Full-screen colour flash (awakenings, counters, boss kill). */
  flash(color: THREE.ColorRepresentation, peak = 0.35, duration = 0.25): void {
    this.grade.flashColor = new THREE.Color(color);
    this.flashPeak = this.flashReduction ? peak * 0.25 : peak;
    this.flashDur = duration;
    this.flashT = duration;
  }

  render(dt: number): void {
    this.govern(performance.now());
    if (this.flashT > 0) {
      this.flashT = Math.max(0, this.flashT - dt);
      this.grade.flash = this.flashPeak * (this.flashT / Math.max(1e-3, this.flashDur));
    }
    this.renderer.info.reset();
    this.composer.render(dt);
    const info = this.renderer.info;
    this.stats.calls = info.render.calls;
    this.stats.triangles = info.render.triangles;
    this.stats.programs = info.programs?.length ?? 0;
  }

  get drawCalls(): number {
    return this.stats.calls;
  }

  dispose(): void {
    for (const rt of this.envCache.values()) rt.dispose();
    this.envCache.clear();
    this.pmrem.dispose();
    this.composer.dispose();
    this.renderer.dispose();
  }
}
